/**
 * The read API envelope.
 *
 * Source of truth: build plan section 5.10. One batched call powers the entire
 * Overview, which is what makes "no tile can disagree with another"
 * structurally true rather than a promise: every number comes from one read,
 * over one range, at one instant.
 *
 * There is deliberately no `tz` field in the request. The reporting timezone is
 * a property of the org, resolved server side, and echoed in `meta`.
 */
import { z } from 'zod';
import { ENVIRONMENTS } from './keys.js';
import { DEFAULT_FUNNEL_WINDOW_SECONDS, HLL_ERROR_PCT, HLL_METHOD, MAX_FUNNEL_DROPOFF_ROWS, MAX_FUNNEL_STEPS, MAX_FUNNEL_WINDOW_SECONDS, MIN_FUNNEL_STEPS, MIN_FUNNEL_WINDOW_SECONDS, } from './limits.js';
import { isIso8601 } from './event.js';
/* ------------------------------------------------------------------------- *
 * Vocabulary.
 * ------------------------------------------------------------------------- */
/** Bucket widths the rollup writes, per 5.2.1. */
export const GRANULARITIES = ['hour', 'day'];
/** Which store answered. `cold` means DuckDB over Parquet and a lagging `as_of`. */
export const QUERY_TIERS = ['hot', 'cold'];
/** Metrics a tile may request. */
export const METRICS = [
    'events',
    'unique_persons',
    'sessions',
    /**
     * Bot events in the range: the subset of `events` the collector flagged as
     * automated.
     *
     * Exists so the two headline numbers can be reconciled. `events` counts bots
     * and `unique_persons` does not (they are excluded from the sketch at write
     * time and cannot be removed afterwards), so on a crawled site the ratio
     * between them looks broken until you can see the split.
     */
    'bot_events',
];
/** Tile shapes the batched query understands. */
export const TILE_KINDS = ['timeseries', 'scalar', 'top', 'sessions'];
/**
 * Why a response carries no data. `null` means it does carry data. Naming the
 * reason is what stops an empty dashboard reading as a broken dashboard.
 */
export const EMPTY_REASONS = [
    'no_events',
    'no_match',
    'no_traffic_in_env',
    'range_too_large',
    /**
     * The cold tier could not read part of the range because compaction has not
     * reached it yet. This is NOT a measurement: it is the absence of one.
     *
     * It exists because the alternative is worse than an error. A funnel over a
     * range the lake has not compacted resolved to an empty file list, every step
     * computed zero, and the screen drew a real looking funnel where nobody had
     * converted. An empty result and a true zero were the same bytes, so nothing
     * downstream could tell them apart.
     */
    'awaiting_compaction',
    /**
     * The People list was asked for identified people and this project has none.
     *
     * Distinct from `no_traffic_in_env`, which sends the reader to check their
     * write key. Here the write key is fine, the events are arriving, and the
     * answer is that nothing has ever called `identify()`. Telling somebody to
     * debug their installation when the fix is one SDK call is the kind of empty
     * state that costs an afternoon.
     */
    'no_identified_people',
];
const EnvironmentSchema = z.enum(ENVIRONMENTS);
const Iso8601Schema = z
    .string()
    .refine(isIso8601, { message: 'expected an ISO-8601 instant with a timezone' });
/* ------------------------------------------------------------------------- *
 * Request.
 * ------------------------------------------------------------------------- */
const TileIdSchema = z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z0-9_]+$/, {
    message: 'tile ids are lower case, digits and underscores',
});
export const TimeseriesTileSchema = z.object({
    id: TileIdSchema,
    kind: z.literal('timeseries'),
    metric: z.enum(METRICS),
});
export const ScalarTileSchema = z.object({
    id: TileIdSchema,
    kind: z.literal('scalar'),
    metric: z.enum(METRICS),
});
export const TopTileSchema = z.object({
    id: TileIdSchema,
    kind: z.literal('top'),
    metric: z.enum(METRICS),
    limit: z.number().int().min(1).max(100),
});
export const SessionsTileSchema = z.object({
    id: TileIdSchema,
    kind: z.literal('sessions'),
    limit: z.number().int().min(1).max(100),
});
export const TileSchema = z.discriminatedUnion('kind', [
    TimeseriesTileSchema,
    ScalarTileSchema,
    TopTileSchema,
    SessionsTileSchema,
]);
/** `POST /v1/projects/{projectId}/query:batch`. */
export const QueryBatchRequestSchema = z
    .object({
    env: EnvironmentSchema,
    from: Iso8601Schema,
    to: Iso8601Schema,
    granularity: z.enum(GRANULARITIES),
    tiles: z.array(TileSchema).min(1),
    /**
     * Route unique counts to the exact DuckDB path instead of the HyperLogLog
     * sketches. Honoured for ranges of at most EXACT_UNIQUES_MAX_RANGE_DAYS and
     * answered with `approximate: false`.
     */
    exact_uniques: z.boolean().optional(),
})
    .superRefine((request, ctx) => {
    if (Date.parse(request.from) >= Date.parse(request.to)) {
        ctx.addIssue({
            code: 'custom',
            path: ['from'],
            message: 'from must be strictly before to',
        });
    }
    const seen = new Set();
    request.tiles.forEach((tile, index) => {
        if (seen.has(tile.id)) {
            ctx.addIssue({
                code: 'custom',
                path: ['tiles', index, 'id'],
                message: 'tile ids must be unique within a request',
            });
        }
        seen.add(tile.id);
    });
});
/* ------------------------------------------------------------------------- *
 * Response.
 * ------------------------------------------------------------------------- */
export const QueryMetaSchema = z.object({
    /** The instant the numbers describe. Lags for the cold tier. */
    as_of: Iso8601Schema,
    tier: z.enum(QUERY_TIERS),
    granularity: z.enum(GRANULARITIES),
    env: EnvironmentSchema,
    project_id: z.string().min(1),
    /** The org's timezone, resolved server side and echoed so the client never
     *  has to guess which day "yesterday" was. */
    reporting_timezone: z.string().min(1),
    query_ms: z.number().int().min(0),
});
/**
 * Approximation disclosure, attached to any HyperLogLog derived result.
 * Rendered as a glyph with a tooltip stating the method and the error bound. A
 * PM who compares "1,204 unique users" to a manual count of 1,211, finds no
 * explanation, files a bug and stops trusting every other number on the page.
 */
const approximation = {
    approximate: z.boolean().optional(),
    error_pct: z.number().min(0).optional(),
    method: z.string().min(1).optional(),
};
export const TimeseriesPointSchema = z.object({
    /** Bucket label: `YYYY-MM-DD` at day granularity, `YYYY-MM-DDTHH` at hour. */
    t: z.string().min(1),
    v: z.number(),
});
export const TimeseriesResultSchema = z.object({
    points: z.array(TimeseriesPointSchema),
    ...approximation,
});
export const ScalarResultSchema = z.object({
    value: z.number(),
    ...approximation,
});
export const TopRowSchema = z.object({
    name: z.string(),
    events: z.number().int().min(0),
    persons: z.number().int().min(0),
});
export const TopResultSchema = z.object({
    rows: z.array(TopRowSchema),
    ...approximation,
});
export const SessionRowSchema = z.object({
    session_id: z.string().min(1),
    /**
     * Empty for a session whose visitor has not identified.
     *
     * This was `min(1)`, and it rejected the whole batch response the moment the
     * read plane started returning anonymous sessions: one row in a hundred with
     * an empty identity took out the entire Sessions screen with "the API
     * returned an unexpected shape". The screen showed nothing at all rather
     * than ninety nine rows and a gap, which is the worst possible trade.
     *
     * An anonymous visitor genuinely has no distinct id until they identify, so
     * this is a normal value and not a degraded one. The identifier is still
     * required to BE a string, so a caller can render it without a null check;
     * it is simply allowed to be empty, and the session list renders that as
     * "anonymous" rather than linking to a person page that cannot open.
     */
    distinct_id: z.string(),
    /**
     * The person's display name, resolved server side from their profile
     * properties, or null when nothing identifies them.
     *
     * Resolved on the server, not here, because the People list is ordered and
     * paged by `gsi1sk` inside DynamoDB: a name computed in the browser would
     * sort the current page only.
     *
     * The `distinct_id` is always sent as well and is never replaced by this.
     * Support needs the exact value the SDK sent, and a friendly name is
     * ambiguous because two people can be called "Ada".
     */
    display_name: z.string().nullish(),
    started_at: Iso8601Schema,
    /**
     * When the last event in the session arrived, or null for a session the
     * rollup has not closed yet.
     *
     * Nullable rather than absent because a session in progress is a normal
     * state, not a degraded one, and the list has to be able to say "still
     * going" instead of printing a start time and implying it ended.
     */
    ended_at: Iso8601Schema.nullish(),
    /**
     * The first path this session was seen on: its landing page.
     *
     * Written by the rollup and read by the session query, then dropped on the
     * floor before the response for the whole life of the product. It is the
     * single most useful column the Sessions screen was missing, because "where
     * did this visit start" is the first question anyone asks of a session list.
     */
    entry_path: z.string().nullish(),
    duration_s: z.number().min(0),
    event_count: z.number().int().min(0),
    has_replay: z.boolean(),
});
export const SessionsResultSchema = z.object({
    rows: z.array(SessionRowSchema),
});
/** One entry of `results`, keyed by the tile id that asked for it. */
export const TileResultSchema = z.union([
    TimeseriesResultSchema,
    ScalarResultSchema,
    TopResultSchema,
    SessionsResultSchema,
]);
export const QueryBatchResponseSchema = z.object({
    meta: QueryMetaSchema,
    results: z.record(z.string(), TileResultSchema),
    /** Null when the response carries data. */
    empty_reason: z.enum(EMPTY_REASONS).nullable(),
});
/* ------------------------------------------------------------------------- *
 * Helpers.
 * ------------------------------------------------------------------------- */
/** The approximation disclosure carried by every HyperLogLog derived result. */
export const HLL_DISCLOSURE = Object.freeze({
    approximate: true,
    error_pct: HLL_ERROR_PCT,
    method: HLL_METHOD,
});
/**
 * Validate a batched read response and, on success, assert the structural
 * invariant that gives the Overview its coherence: every requested tile id is
 * present in `results`, and no unrequested id appears.
 */
export function resultsCoverTiles(request, response) {
    const requested = new Set(request.tiles.map((tile) => tile.id));
    const returned = new Set(Object.keys(response.results));
    if (requested.size !== returned.size)
        return false;
    for (const id of requested) {
        if (!returned.has(id))
            return false;
    }
    return true;
}
/* ------------------------------------------------------------------------- *
 * Funnels (plan FR-DASH-6).
 *
 * The funnel is answered by the cold tier as an async job, not as a tile, and
 * these shapes mirror services/api/schema/read_models.py field for field.
 *
 * They are declared here for a reason worth stating: the backend has computed
 * funnels since it was built and shipped with no TypeScript shape at all, so
 * the only screen that could have used it could not be written without inventing
 * one. An invented shape is how `conversion_pct` came to be declared server side
 * for a field the query plane has never sent.
 * ------------------------------------------------------------------------- */
/**
 * What a cold answer is actually based on.
 *
 * Present on every cold result. Without it a range the lake has not compacted
 * and a range in which nobody converted are the same numbers, and a funnel over
 * unread days renders as a real funnel where everybody dropped out.
 */
export const CoverageSchema = z.object({
    requested_days: z.number().int().nonnegative(),
    covered_days: z.number().int().nonnegative(),
    /** Named up to a cap; `unread_day_count` is the untruncated figure. */
    unread_days: z.array(z.string()),
    unread_day_count: z.number().int().nonnegative(),
    /** Newest manifest write time among the days read, so a partial day shows. */
    covered_through: z.string().nullable(),
    complete: z.boolean(),
});
/** One step, exactly the three fields services/query emits. */
export const FunnelStepRowSchema = z.object({
    step_index: z.number().int().nonnegative(),
    name: z.string(),
    persons: z.number().int().nonnegative(),
});
/** One person who reached a step and not the next one. */
export const FunnelDropoffRowSchema = z.object({
    distinct_id: z.string(),
    reached_step: z.number().int().nonnegative(),
    /**
     * Resolved by the read plane, not the query plane: the drop off rows come out
     * of DuckDB over Parquet, which holds no person profile. Optional because the
     * resolution is capped, and null for anyone who never identified.
     */
    display_name: z.string().nullish(),
});
export const FunnelResultSchema = z.object({
    steps: z.array(FunnelStepRowSchema),
    window_seconds: z.number().int().positive(),
    scanned_files: z.number().int().nonnegative(),
    dropoff: z.array(FunnelDropoffRowSchema).nullable(),
    coverage: CoverageSchema,
});
export const FunnelPreviewRequestSchema = z
    .object({
    env: EnvironmentSchema,
    from: Iso8601Schema,
    to: Iso8601Schema,
    steps: z.array(z.string().min(1).max(200)).min(MIN_FUNNEL_STEPS).max(MAX_FUNNEL_STEPS),
    window_seconds: z
        .number()
        .int()
        .min(MIN_FUNNEL_WINDOW_SECONDS)
        .max(MAX_FUNNEL_WINDOW_SECONDS)
        .default(DEFAULT_FUNNEL_WINDOW_SECONDS),
    dropoff_step: z.number().int().min(0).max(MAX_FUNNEL_STEPS - 1).nullish(),
    dropoff_limit: z.number().int().min(1).max(MAX_FUNNEL_DROPOFF_ROWS).default(100),
})
    .refine((value) => new Date(value.from) < new Date(value.to), {
    message: 'from must be strictly before to',
    path: ['to'],
})
    .refine((value) => value.dropoff_step === null ||
    value.dropoff_step === undefined ||
    value.dropoff_step < value.steps.length, {
    // Server side this is a 400. Catching it here means the person building
    // the funnel finds out while they are building it.
    message: 'dropoff_step must name a step in this funnel',
    path: ['dropoff_step'],
});
export const JOB_STATUSES = ['queued', 'running', 'done', 'error'];
/**
 * An async query job.
 *
 * `result` is deliberately unknown rather than a union keyed on `op`. The server
 * returns it unvalidated (`jobs._decode_result` is a bare `json.loads`), so a
 * union here would be a promise this side cannot keep. Each caller parses the
 * payload with the schema for the operation it asked for, which is also what
 * keeps a funnel screen from silently rendering an export result.
 */
export const JobResponseSchema = z.object({
    job_id: z.string().min(1),
    status: z.enum(JOB_STATUSES),
    op: z.string(),
    created_at: z.string(),
    started_at: z.string().nullish(),
    finished_at: z.string().nullish(),
    query_ms: z.number().nullish(),
    meta: QueryMetaSchema.nullish(),
    result: z.unknown().nullish(),
    error_code: z.string().nullish(),
    error_message: z.string().nullish(),
});
/**
 * Conversion percentages, computed here rather than transmitted.
 *
 * The lake stores person counts per step and nothing else. Both percentages are
 * a division, so sending them would be sending a derived number that can
 * disagree with the one beside it after a rounding change.
 *
 * Guards divide by zero at both ends: a funnel whose first step nobody reached
 * is a legitimate answer, not a NaN.
 */
export function funnelConversions(steps) {
    const first = steps[0]?.persons ?? 0;
    return steps.map((step, index) => {
        const previous = index === 0 ? step.persons : (steps[index - 1]?.persons ?? 0);
        return {
            conversionPct: first === 0 ? 0 : (step.persons / first) * 100,
            stepConversionPct: previous === 0 ? 0 : (step.persons / previous) * 100,
        };
    });
}
/* ------------------------------------------------------------------------- *
 * Heatmaps, ADR 0034.
 *
 * `GET /v1/projects/{project_id}/heatmaps` and
 * `GET /v1/projects/{project_id}/heatmaps/page`. Both are answered from the cold
 * tier, so they carry `coverage`: clicks appear after the nightly compaction.
 * Mirrored by services/api/schema/read_models.py.
 * ------------------------------------------------------------------------- */
export const HEATMAP_DEVICES = ['desktop', 'tablet', 'mobile'];
export const HeatmapDeviceSchema = z.enum(HEATMAP_DEVICES);
/** Attention and reach are per twentieth of the document. */
export const HEATMAP_ATTENTION_BANDS = 20;
const count = z.number().int().nonnegative();
export const HeatmapPageRowSchema = z.object({
    path: z.string(),
    clicks: count,
    rage_clicks: count,
    dead_clicks: count,
    sessions: count,
});
export const HeatmapPagesResponseSchema = z.object({
    meta: QueryMetaSchema,
    pages: z.array(HeatmapPageRowSchema),
    coverage: CoverageSchema,
});
export const HeatmapTotalsSchema = z.object({
    clicks: count,
    rage_clicks: count,
    dead_clicks: count,
    sessions: count,
    /** Page views that reported scroll, the denominator of `scroll_reach`. */
    views: count,
});
export const HeatmapElementSchema = z.object({
    selector: z.string(),
    /** Most common visible label. Null for inputs and masked regions. */
    text: z.string().nullable(),
    tag: z.string().nullable(),
    clicks: count,
    rage_clicks: count,
    dead_clicks: count,
    sessions: count,
});
/** A cell inside one element: `x` and `y` are 0 to 49 on a 50 by 50 grid. */
export const HeatmapElementBinSchema = z.object({
    selector: z.string(),
    x: z.number().int().min(0).max(49),
    y: z.number().int().min(0).max(49),
    clicks: count,
    rage_clicks: count,
    dead_clicks: count,
});
/** A cell of the document: `x` is 0 to 99 percent of its width, `y` a 10 px row. */
export const HeatmapPageBinSchema = z.object({
    x: z.number().int().min(0).max(99),
    y: count,
    clicks: count,
    rage_clicks: count,
    dead_clicks: count,
});
export const PageHeatmapResponseSchema = z.object({
    meta: QueryMetaSchema,
    path: z.string(),
    device: HeatmapDeviceSchema,
    totals: HeatmapTotalsSchema,
    top_elements: z.array(HeatmapElementSchema),
    element_bins: z.array(HeatmapElementBinSchema),
    page_bins: z.array(HeatmapPageBinSchema),
    /** Percent of views reaching each band; empty when no view reported scroll. */
    scroll_reach: z.array(z.number().int().min(0).max(100)),
    /** Mean milliseconds per view each band was on screen; empty as above. */
    attention_ms: z.array(count),
    coverage: CoverageSchema,
});
/**
 * `GET /v1/projects/{project_id}/heatmaps/snapshot`. `snapshot` is null until a
 * browser has captured the page on this device class (AC-12). The URL is a
 * short lived presigned GET, handed only to the sandboxed player (D31).
 */
export const HeatmapSnapshotResponseSchema = z.object({
    path: z.string(),
    device: HeatmapDeviceSchema,
    snapshot: z
        .object({
        url: z.string().url(),
        captured_at: z.string().nullable(),
    })
        .nullable(),
    expires_in: z.number().int().positive(),
});
//# sourceMappingURL=read.js.map